const STORAGE_PREFIX = 'agent_backoffice_auth';

export type AuthTokens = {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  expiresAt?: number;
};

export type AuthConfig = {
  cognitoDomain: string;
  clientId: string;
  redirectUri: string;
  logoutUri: string;
  scopes: string[];
};

const verifierKey = `${STORAGE_PREFIX}_pkce_verifier`;
const stateKey = `${STORAGE_PREFIX}_oauth_state`;
const tokensKey = `${STORAGE_PREFIX}_tokens`;

function getRuntimeRedirectUri(configuredRedirectUri: string): string {
  const { hostname, origin } = window.location;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${origin}/auth/callback`;
  }

  return configuredRedirectUri;
}

function getRuntimeLogoutUri(configuredLogoutUri: string): string {
  const { hostname, origin } = window.location;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${origin}/`;
  }

  return configuredLogoutUri;
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

function base64UrlEncode(input: ArrayBuffer | Uint8Array): string {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  let binary = '';

  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });

  return window
    .btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function sha256(value: string): Promise<ArrayBuffer> {
  const encoded = new TextEncoder().encode(value);
  return window.crypto.subtle.digest('SHA-256', encoded);
}

function assertAuthConfig(config: AuthConfig): void {
  const missing = [
    ['REACT_APP_COGNITO_DOMAIN', config.cognitoDomain],
    ['REACT_APP_COGNITO_CLIENT_ID', config.clientId],
    ['REACT_APP_COGNITO_REDIRECT_URI', config.redirectUri],
    ['REACT_APP_COGNITO_LOGOUT_URI', config.logoutUri],
  ].filter(([, value]) => !value);

  if (missing.length > 0) {
    throw new Error(`Missing auth config: ${missing.map(([key]) => key).join(', ')}`);
  }
}

export function getStoredTokens(): AuthTokens | null {
  const rawTokens = window.sessionStorage.getItem(tokensKey);

  if (!rawTokens) {
    return null;
  }

  try {
    const tokens = JSON.parse(rawTokens) as AuthTokens;
    const expiresAt = tokens.expiresAt || 0;

    if (!tokens.accessToken || (expiresAt > 0 && expiresAt <= Date.now())) {
      clearAuthSession();
      return null;
    }

    return tokens;
  } catch {
    clearAuthSession();
    return null;
  }
}

/**
 * Reads a token's claims without verifying it.
 *
 * Only ever used for claims the UI reads about itself — the address to show in the header,
 * and which stages to offer in the switcher. FES verifies the signature on every request, so
 * a tampered token buys nothing here beyond a switcher entry whose stage then answers 403.
 */
function decodeJwtClaims(token?: string): Record<string, unknown> | undefined {
  if (!token) return undefined;

  const payload = token.split('.')[1];
  if (!payload) return undefined;

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const binary = window.atob(padded);
    const json = decodeURIComponent(
      Array.from(binary, character => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')
    );
    const claims = JSON.parse(json);
    return claims && typeof claims === 'object' ? (claims as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
}

export function getEmailFromIdToken(idToken?: string): string | undefined {
  const email = decodeJwtClaims(idToken)?.email;
  return typeof email === 'string' && email ? email : undefined;
}

/**
 * Cognito omits `cognito:groups` entirely when a user is in no group, rather than sending an
 * empty array, so an absent claim and an empty one mean the same thing here.
 */
export function getGroupsFromAccessToken(accessToken?: string): string[] {
  const groups = decodeJwtClaims(accessToken)?.['cognito:groups'];
  return Array.isArray(groups) ? groups.filter((group): group is string => typeof group === 'string') : [];
}

export async function redirectToLogin(config: AuthConfig): Promise<void> {
  assertAuthConfig(config);

  const verifier = randomBase64Url(64);
  const state = randomBase64Url(32);
  const challenge = base64UrlEncode(await sha256(verifier));
  const redirectUri = getRuntimeRedirectUri(config.redirectUri);
  const authorizationUrl = new URL('/oauth2/authorize', config.cognitoDomain);

  window.sessionStorage.setItem(verifierKey, verifier);
  window.sessionStorage.setItem(stateKey, state);

  authorizationUrl.search = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: config.scopes.join(' '),
    redirect_uri: redirectUri,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  }).toString();

  window.location.assign(authorizationUrl.toString());
}

export async function exchangeCodeForTokens(config: AuthConfig, code: string, state: string): Promise<AuthTokens> {
  assertAuthConfig(config);

  const expectedState = window.sessionStorage.getItem(stateKey);
  const verifier = window.sessionStorage.getItem(verifierKey);

  if (!expectedState || expectedState !== state) {
    throw new Error('Invalid OAuth state returned by Cognito.');
  }

  if (!verifier) {
    throw new Error('Missing PKCE verifier for Cognito token exchange.');
  }

  const redirectUri = getRuntimeRedirectUri(config.redirectUri);
  const response = await window.fetch(new URL('/oauth2/token', config.cognitoDomain).toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: redirectUri,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Cognito token exchange failed (${response.status}): ${errorBody}`);
  }

  const tokenResponse = await response.json();
  const expiresIn = Number(tokenResponse.expires_in || 0);
  const tokens: AuthTokens = {
    accessToken: tokenResponse.access_token,
    idToken: tokenResponse.id_token,
    refreshToken: tokenResponse.refresh_token,
    expiresAt: expiresIn > 0 ? Date.now() + expiresIn * 1000 : undefined,
  };

  window.sessionStorage.setItem(tokensKey, JSON.stringify(tokens));
  window.sessionStorage.removeItem(verifierKey);
  window.sessionStorage.removeItem(stateKey);

  return tokens;
}

export function clearAuthSession(): void {
  window.sessionStorage.removeItem(tokensKey);
  window.sessionStorage.removeItem(verifierKey);
  window.sessionStorage.removeItem(stateKey);
}

export function redirectToLogout(config: AuthConfig): void {
  clearAuthSession();

  if (!config.cognitoDomain || !config.clientId || !config.logoutUri) {
    window.location.assign('/');
    return;
  }

  const logoutUrl = new URL('/logout', config.cognitoDomain);

  logoutUrl.search = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: getRuntimeLogoutUri(config.logoutUri),
  }).toString();

  window.location.assign(logoutUrl.toString());
}
